/**
 * DAS-41: 대시보드 상단 점검 배너
 * - getActiveMaintenance() 폴링 (5분 간격)
 * - 활성 점검 있으면 거래소명 + 종료 예정 시간 표시
 * - 점검 없으면 null 반환 (렌더링 없음)
 * - API 에러 시 배너 숨김 (크래시 방지)
 *
 * DAS-49: fixed 포지셔닝이 본문을 가리는 문제 해결
 * - 배너 wrapper의 실제 높이를 ResizeObserver로 측정해
 *   `--maintenance-banner-h` CSS 변수로 노출
 * - 각 페이지의 <main>이 해당 변수만큼 marginTop을 적용해
 *   본문이 배너 아래로 자연스럽게 밀리도록 함
 */
import React from "react";
import dayjs from "dayjs";
import { getActiveMaintenance } from "../../services/maintenanceApi";
import { getNavCollapsed } from "../../utils/navState";

const BANNER_HEIGHT_VAR = "--maintenance-banner-h";

const setBannerHeightVar = (px) => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${px}px`);
};

const resetBannerHeightVar = () => setBannerHeightVar(0);

class MaintenanceBanner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeItems: [],
            now: Date.now(),
            collapsed: getNavCollapsed(),
        };
        this.pollingInterval = null;
        this.countdownInterval = null;
        this.bannerRef = React.createRef();
        this.resizeObserver = null;
        this._onStorage = () => this.setState({ collapsed: getNavCollapsed() });
    }

    componentDidMount() {
        this.fetchActive();
        this.pollingInterval = setInterval(this.fetchActive, 300_000);
        this.countdownInterval = setInterval(() => {
            this.setState({ now: Date.now(), collapsed: getNavCollapsed() });
        }, 1_000);
        window.addEventListener("storage", this._onStorage);
        this.attachResizeObserver();
    }

    componentDidUpdate(prevProps, prevState) {
        const wasVisible = prevState.activeItems && prevState.activeItems.length > 0;
        const isVisible = this.state.activeItems && this.state.activeItems.length > 0;
        if (wasVisible && !isVisible) {
            resetBannerHeightVar();
        }
        // activeItems 변경으로 배너가 새로 마운트/언마운트되는 경우 ref 재바인딩
        this.attachResizeObserver();
        this.measureBannerHeight();
    }

    componentWillUnmount() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
        window.removeEventListener("storage", this._onStorage);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        resetBannerHeightVar();
    }

    attachResizeObserver() {
        if (typeof ResizeObserver === "undefined") return;
        const node = this.bannerRef.current;
        if (!node) return;
        // 이미 동일 노드에 옵저버가 붙어 있으면 재바인딩 불필요
        if (this._observedNode === node && this.resizeObserver) return;
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.resizeObserver = new ResizeObserver(() => this.measureBannerHeight());
        this.resizeObserver.observe(node);
        this._observedNode = node;
    }

    measureBannerHeight() {
        const node = this.bannerRef.current;
        if (!node) {
            resetBannerHeightVar();
            return;
        }
        const h = node.offsetHeight || 0;
        setBannerHeightVar(h);
    }

    formatCountdown(startAt) {
        const diff = dayjs(startAt).valueOf() - this.state.now;
        if (diff <= 0) return "곧 시작";
        const hours = Math.floor(diff / 3_600_000);
        const minutes = Math.floor((diff % 3_600_000) / 60_000);
        const seconds = Math.floor((diff % 60_000) / 1_000);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)} 후 시작`;
    }

    fetchActive = async () => {
        try {
            const res = await getActiveMaintenance();
            this.setState({ activeItems: Array.isArray(res.data) ? res.data : [] });
        } catch (_e) {
            this.setState({ activeItems: [] });
        }
    };

    renderBannerGroup(items, isActive) {
        if (!items || items.length === 0) return null;

        const containerCls = isActive
            ? "w-full border-y-2 border-warning/40 bg-warning/15 px-4 py-3 shadow-sm"
            : "w-full border-y-2 border-warning/25 bg-warning-subtle px-4 py-3 shadow-sm";
        const iconBgCls = isActive ? "bg-warning/25" : "bg-warning/15";
        const icon = isActive ? "🔧" : "❗";
        const titleCls = isActive ? "text-warning" : "text-warning-light";
        const title = isActive
            ? "거래소 서버 점검중 — 해당 거래소의 실시간 값은 점검 직전 스냅샷으로 고정됩니다"
            : "거래소 서버 점검 예정 — 아래 시간에 점검이 시작됩니다";
        const detailCls = isActive ? "text-warning/90" : "text-warning-light/80";
        const dotCls = isActive ? "text-warning/60" : "text-warning/40";
        const reasonCls = isActive ? "text-warning/70" : "text-warning/50";

        return (
            <div role="status" aria-live="polite" className={containerCls}>
                <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${iconBgCls} text-lg ${isActive ? "animate-pulse" : ""}`}>
                        {icon}
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${titleCls}`}>
                            {title}
                        </div>
                        <div className={`mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs ${detailCls}`}>
                            {items.map((item) => (
                                <span key={item.id} className="font-medium flex items-center gap-1">
                                    <span className="uppercase">{item.exchange}</span>
                                    <span className={`mx-0.5 ${dotCls}`}>·</span>
                                    {isActive
                                        ? `${dayjs(item.end_at).format("YYYY-MM-DD HH:mm")} 종료 예정`
                                        : (
                                            <>
                                                <span className="tabular-nums tracking-tight font-semibold text-warning">
                                                    {this.formatCountdown(item.start_at)}
                                                </span>
                                                <span className={`${dotCls}`}>·</span>
                                                <span className="text-warning/40">
                                                    {dayjs(item.start_at).format("MM/DD HH:mm")}
                                                </span>
                                            </>
                                        )
                                    }
                                    {item.reason && <span className={`ml-1 ${reasonCls}`}>({item.reason})</span>}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { activeItems } = this.state;
        if (!activeItems || activeItems.length === 0) {
            resetBannerHeightVar();
            return null;
        }

        const active = activeItems.filter((i) => i.status === "active");
        const upcoming = activeItems.filter((i) => i.status === "upcoming");

        if (active.length === 0 && upcoming.length === 0) {
            resetBannerHeightVar();
            return null;
        }

        const isSm = typeof window !== "undefined" && window.innerWidth >= 640;
        const topOffset = isSm ? 0 : 48;
        const leftOffset = isSm ? (this.state.collapsed ? 56 : 220) : 0;

        return (
            <div
                ref={this.bannerRef}
                className="fixed right-0 z-40 transition-[left] duration-200"
                style={{ top: topOffset, left: leftOffset }}
            >
                {this.renderBannerGroup(active, true)}
                {this.renderBannerGroup(upcoming, false)}
            </div>
        );
    }
}

export default MaintenanceBanner;
